import { FC, useEffect, useMemo } from 'react';
import { Col, DatePicker, Row, Skeleton } from 'antd';
import moment, { Moment } from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AppTable, { appTableStyles } from 'components/AppTable';
import { AppTableColumn } from 'components/AppTable/useColumns';
import FilterDropdown from 'components/AppTable/FilterDropdown';
import {
  convertMinsToHrsMins,
  ellipseText,
  formatCompletedAtColumn,
  parseDateToUSFormat,
  renderTime,
} from 'utils/string.utils';
import appRoutes from 'config/appRoutes';
import { RootState } from 'store';
import { profileTimelogSlice } from '../../store/profileTimelog';
import { ProfileDateRangeProps } from './useProfileDateRange';
import PeriodShiftButtons from './PeriodShiftButtons';

const { RangePicker } = DatePicker;

const DATE_FORMAT = 'YYYY-MM-DD';

const RANGE_PRESETS: Record<string, [Moment, Moment]> = {
  'This month': [moment().startOf('month'), moment().endOf('month')],
  'Last 30 days': [moment().subtract(29, 'days'), moment()],
  'This week': [moment().startOf('week'), moment().endOf('week')],
};

interface MyTimelogsProps extends ProfileDateRangeProps {
  userUuid: string;
}

const MyTimelogs: FC<MyTimelogsProps> = ({ userUuid, dateRange, onDateRangeChange }) => {
  const dispatch = useDispatch();
  const totalTime = useSelector((globalState: RootState) => globalState.profileTimelog.totalTime);
  const metrics = useSelector((globalState: RootState) => globalState.profileTimelog.metrics);
  const loading = useSelector((globalState: RootState) => globalState.profileTimelog.loading);
  const silenceLoading = useSelector((globalState: RootState) => globalState.profileTimelog.silenceLoading);

  const isLoading = loading && !silenceLoading;

  // Manual vs real-time split uses the server-calculated counts (meta.metrics from ?with_metrics=1).
  const { manualPercent, timerPercent } = useMemo(() => {
    const manualCount = metrics?.manual.count ?? 0;
    const timerCount = metrics?.realtime.count ?? 0;
    const total = manualCount + timerCount;

    if (!total) {
      return { manualPercent: 0, timerPercent: 0 };
    }

    const manual = Math.round((manualCount / total) * 100);

    return { manualPercent: manual, timerPercent: 100 - manual };
  }, [metrics]);

  const startDate = dateRange[0].format(DATE_FORMAT);
  const endDate = dateRange[1].format(DATE_FORMAT);

  useEffect(() => {
    // Re-scope to the current user, request server-side metrics, and apply the shared date range.
    dispatch(profileTimelogSlice.actions.setFixedParams({ 'filter[user]': userUuid, with_metrics: 1 }));
    dispatch(
      profileTimelogSlice.actions.setFilter({
        date_start: { value: startDate },
        date_end: { value: endDate },
      }),
    );
  }, [dispatch, userUuid, startDate, endDate]);

  const columns: AppTableColumn[] = [
    {
      title: 'Client',
      dataIndex: 'customer_abbreviation',
      sorterKey: 'customer_name',
      sorter: true,
      render: (value, row) => (
        <Link to={`${appRoutes.PROJECTS}/${row.project_uuid}`} style={{ whiteSpace: 'nowrap' }}>
          {row.customer_name}
        </Link>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'source',
      width: 65,
      sorter: true,
      render: (value) => <span style={{ textTransform: 'capitalize' }}>{value}</span>,
    },
    {
      title: 'Manual',
      dataIndex: 'is_manual',
      width: 65,
      exportRender: true,
      sorter: true,
      render: (value) => (value ? 'Yes' : 'No'),
    },
    {
      title: 'Billable',
      dataIndex: 'billable',
      width: 70,
      exportRender: true,
      sorter: true,
      render: (value) => (value === false ? 'No' : 'Yes'),
    },
    {
      title: 'Title',
      dataIndex: 'source_title',
      sorter: true,
      minSpaceText: (value) => ellipseText(value),
      render: (value, row) => (
        <Link
          to={row.source === 'request' ? `${appRoutes.REQUESTS}/${row.source_uuid}` : `?task_uuid=${row.source_uuid}`}
          style={{ whiteSpace: 'nowrap' }}
        >
          {ellipseText(value)}
        </Link>
      ),
    },
    {
      title: 'Tracked',
      dataIndex: 'minutes',
      width: 100,
      exportRender: true,
      sorter: true,
      render: (value) => renderTime(convertMinsToHrsMins(value)),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      exportRender: true,
      sorter: true,
      render: (value) => parseDateToUSFormat(value),
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      width: 300,
      ellipsis: true,
      sorter: true,
      render: (value) => ellipseText(value),
    },
    {
      title: 'Completed at',
      dataIndex: 'completed_at',
      exportRender: true,
      sorter: true,
      render: (value, row) => formatCompletedAtColumn(value, row.date),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      exportRender: true,
      sorter: true,
      render: (value) => parseDateToUSFormat(value, true),
    },
  ];

  return (
    <div className={`${appTableStyles.container} ${appTableStyles.fullPage}`} style={{ padding: 0 }}>
      <Row gutter={[16, 8]} align="middle" justify="space-between">
        <Col flex="auto" className="md-flex-none">
          {isLoading ? (
            <Skeleton.Input active size="large" style={{ width: 360, borderRadius: 3 }} />
          ) : (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 16px',
                border: '1px solid rgba(0, 68, 204, 0.2)',
                borderRadius: 3,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              }}
            >
              <span style={{ fontWeight: 600 }}>Time (%)</span>
              <span><span className="danger">{manualPercent}%</span> manual entry</span>
              <span><span className="green">{timerPercent}%</span> real-time entry</span>
            </div>
          )}
        </Col>

        <Col>
          <Row gutter={[16, 8]} align="middle">
            <Col xs={24} sm={12} className="md-flex-auto lg-flex-none">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <PeriodShiftButtons dateRange={dateRange} onDateRangeChange={onDateRangeChange} />
                <RangePicker
                  size="large"
                  allowClear={false}
                  format="MM/DD/YYYY"
                  value={dateRange}
                  ranges={RANGE_PRESETS}
                  onChange={(dates: any) => {
                    if (dates && dates[0] && dates[1]) {
                      onDateRangeChange([dates[0], dates[1]]);
                    }
                  }}
                />
              </div>
            </Col>

            <Col xs={24} sm={12} className="md-flex-auto lg-flex-none">
              <FilterDropdown
                storeSlice={profileTimelogSlice}
                label="Filters"
                showCount
                multiple
                options={[
                  {
                    type: 'group',
                    key: 'is_manual',
                    title: 'By entry',
                    single: true,
                    children: [
                      { label: 'Manual only', value: true, key: 'is_manual' },
                      { label: 'Timer only', value: false, key: 'is_manual' },
                    ],
                  },
                  { type: 'divider' },
                  {
                    type: 'group',
                    key: 'category',
                    title: 'By category',
                    single: true,
                    children: [
                      { label: 'Billable', value: 'billable', key: 'category' },
                      { label: 'Non-billable', value: 'non-billable', key: 'category' },
                    ],
                  },
                  {
                    type: 'group',
                    key: 'source',
                    title: 'By type',
                    children: [
                      { label: 'Task', value: 'task', key: 'source' },
                      { label: 'Request', value: 'request', key: 'source' },
                    ],
                  },
                ]}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <AppTable
        storeSlice={profileTimelogSlice}
        widthX={650}
        prefixPagination={totalTime ? `${totalTime} / ` : undefined}
        textCount="timelogs found"
        columns={columns}
        hideActionColumn
        hideRowSelection
        fullPage
      />
    </div>
  );
};

export default MyTimelogs;
